import { Component, inject, Injector, OnInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { environment } from "../../../environments/environment";
import { _Object, GetObjectTaggingCommand, ListObjectsV2Command, S3Client } from "@aws-sdk/client-s3";
import { S3File, S3FileTag } from "../../../models/s3file";
import { BaseComponent } from 'src/app/shared/baseComponent';
import { AuthService } from 'src/app/services/auth.service';
import { Database, get, ref, remove, set } from "@angular/fire/database";
import { S3Connector } from '../S3Connector';

@Component({
  selector: 'app-virtex-assets',
  templateUrl: './textures.component.html',
  styleUrls: ['./textures.component.css']
})
export class TexturesComponent extends BaseComponent implements OnInit {
  database = inject(Database);
  isAuthenticatedWithFirebase = false
  private readonly s3Client: S3Client
  s3Connector: S3Connector;
  files_ground: S3File[] = [];
  files_skybox: S3File[] = [];
  files_textures: S3File[] = [];
  files_groundHeight: S3File[] = [];

  groupedFiles: any[] = [];   // New array to hold grouped files
  
  // Modal properties
  showSkyboxModal: boolean = false;
  selectedSkyboxGroup: any = null;
  
  showImageModal: boolean = false;
  selectedImage: { url: string, title: string, filename: string } | null = null;

  constructor(private injector: Injector, private authService: AuthService, private cdr: ChangeDetectorRef) {
    super(injector)

    this.s3Connector = new S3Connector(injector);

    this.s3Client = new S3Client({
      region: environment.virtexAssets.region,
      credentials: {
        accessKeyId: environment.virtexAssets.access,
        secretAccessKey: environment.virtexAssets.secret
      }
    });
  }

  async ngOnInit(): Promise<void> {

    this.isAuthenticatedWithFirebase = await this.authService.initializeAuth(() => this.onSuccessfullLogin())
    // await this.getData()
  }

  async signInWithGoogle() {
    this.isAuthenticatedWithFirebase = await this.authService.signInWithGoogle()
    if (this.isAuthenticatedWithFirebase) { await this.onSuccessfullLogin() }
  }

  private async onSuccessfullLogin() {
    await this.getData()
  }
  async getData() {
    console.log("TexturesComponent.getData")
    this.loader.show()
    
    // Initialize S3Connector and get files
    await this.s3Connector.init();
    
    // Map S3Connector arrays to component arrays, enriching with needed properties
    this.files_ground = this.enrichFiles(this.s3Connector.groundFiles);
    this.files_skybox = this.enrichFiles(this.s3Connector.skyboxFiles);
    this.files_textures = this.enrichFiles(this.s3Connector.textureFiles);
    this.files_groundHeight = this.enrichFiles(this.s3Connector.groundHeightFiles);
    
    this.loader.hide()
  }

  async loadGroundFiles() {
    console.log("TexturesComponent.loadGroundFiles")
    this.loader.show()
    await this.s3Connector.init();
    this.files_ground = this.enrichFiles(this.s3Connector.groundFiles);
    this.loader.hide()
  }

  async loadSkyboxFiles() {
    console.log("TexturesComponent.loadSkyboxFiles")
    this.loader.show()
    await this.s3Connector.init();
    this.files_skybox = this.enrichFiles(this.s3Connector.skyboxFiles);
    this.loader.hide()
  }

  async loadTextureFiles() {
    console.log("TexturesComponent.loadTextureFiles")
    this.loader.show()
    await this.s3Connector.init();
    this.files_textures = this.enrichFiles(this.s3Connector.textureFiles);
    this.loader.hide()
  }

  async loadGroundHeightFiles() {
    console.log("TexturesComponent.loadGroundHeightFiles")
    this.loader.show()
    await this.s3Connector.init();
    this.files_groundHeight = this.enrichFiles(this.s3Connector.groundHeightFiles);
    this.loader.hide()
  }

  // Enrich S3Connector files with additional properties needed by this component
  private enrichFiles(files: any[]): S3File[] {
    return files.map(file => ({
      Key: file.key,
      Size: file.size,
      GLBFile: this.transformKeyToGlb(file.key),
      LastModified: file.lastModified,
      StorageClass: file.storageClass || 'STANDARD',
      ETag: file.eTag || '',
      CanPreview: this.isImageFile(file.key),
      Tags: [],
      DownloadUrl: this.generateDownloadUrl(file.key),
      BucketUrl: this.generateBucketUrl(environment.virtexAssets.bucket, file.key)
    }));
  }

  async loadFilesMetadataOnly(path: string) {
    console.log("TexturesComponent.loadFilesMetadataOnly " + path)
    let bucketName = environment.virtexAssets.bucket;
    let newFiles: S3File[] = []
    try {
      const response = await this.s3Client.send(new ListObjectsV2Command({
        Bucket: bucketName,
        Prefix: path
      }))

      let contents = response.Contents

      if (contents && contents.length > 0) {
        const files = this.excludeDirectories(contents)

        for (const file of files) {
          // Create file object without loading tags (much faster)
          let ff = {
            Key: file.Key!!,
            Size: file.Size!!,
            GLBFile: this.transformKeyToGlb(file.Key!!),
            LastModified: file.LastModified!!,
            StorageClass: file.StorageClass!!,
            ETag: file.ETag!!,
            CanPreview: this.isImageFile(file.Key!!),
            Tags: [], // Skip expensive tag loading for faster initial load
            DownloadUrl: this.generateDownloadUrl(file.Key!!),
            BucketUrl: this.generateBucketUrl(bucketName, file.Key!!)
          }
          newFiles.push(ff)
        }
      }

    } catch (error) {
      console.error('Error listing files:', error);
    } finally {
      console.log('Files listed (metadata only):', newFiles.length, 'files');
    }
    return newFiles
  }

  async loadFiles(path: string) {
    console.log("TexturesComponent.loadFiles " + path)
    let bucketName = environment.virtexAssets.bucket;
    let newFiles: S3File[] = []
    try {
      const response = await this.s3Client.send(new ListObjectsV2Command({
        Bucket: bucketName,
        Prefix: path
      }))

      let contents = response.Contents

      if (contents && contents.length > 0) {
        const files = this.excludeDirectories(contents)

        for (const file of files) {
          // The s3 type definition have all these fields as optional, but we know they are always present
          let ff = {
            Key: file.Key!!,
            Size: file.Size!!,
            GLBFile: this.transformKeyToGlb(file.Key!!),
            LastModified: file.LastModified!!,
            StorageClass: file.StorageClass!!,
            ETag: file.ETag!!,
            CanPreview: this.isImageFile(file.Key!!),
            Tags: await this.getTags(bucketName, file.Key!!),
            DownloadUrl: this.generateDownloadUrl(file.Key!!),
            BucketUrl: this.generateBucketUrl(bucketName, file.Key!!)
          }
          newFiles.push(ff)

        }
      }

    } catch (error) {
      console.error('Error listing files:', error);
    } finally {
      console.log('Files listed:', newFiles);
    }
    return newFiles
  }

  private transformKeyToGlb(key: string) {
    // Extract the file name without the folder paths
    const fileName = key.substring(key.lastIndexOf('/') + 1)
    // Replace the file extension with .glb
    return fileName.replace(/\.[^/.]+$/, '.glb')
  }
  private generateDownloadUrl(key: string): string {
    return `https://virtex-v2.cellpodium.com/${key}`;
  }

  private generateBucketUrl(bucketName: string, key: string): string {
    return `${environment.virtexAssets.bucketUrl}/${bucketName}?region=${environment.virtexAssets.region}&bucketType=general&prefix=${key}`
  }

  private excludeDirectories(files: _Object[]): _Object[] {
    return files.filter(file => {
      let isDirectory = file.Key?.endsWith('/');
      return !isDirectory;
    })
  }

  private async getTags(bucketName: string, key: string): Promise<S3FileTag[]> {
    const tagSet = (await this.s3Client.send(
      new GetObjectTaggingCommand({
        Bucket: bucketName,
        Key: key
      })
    )
    )?.TagSet

    const s3Tags: S3FileTag[] = []

    if (!tagSet) {
      console.log(`No tags for object ${key}`);
    } else {
      for (const tag of tagSet) {
        s3Tags.push({
          Key: tag.Key,
          Value: tag.Value
        })
      }
    }

    return s3Tags
  }

  // Load tags on-demand for a specific file
  async loadTagsForFile(file: S3File): Promise<void> {
    if (file.Tags.length === 0) {
      console.log("Loading tags for:", file.Key)
      file.Tags = await this.getTags(environment.virtexAssets.bucket, file.Key);
    }
  }

  private isImageFile(filename: string): boolean {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.tiff']
    const lowercasedFilename = filename.toLowerCase()
    return imageExtensions.some(extension => lowercasedFilename.endsWith(extension))
  }

  protected readonly environment = environment;

  getSizeFormatted(size: number): string {
    const megabytes = size / (1024 * 1024)
    if (megabytes < 1) {
      const kilobytes = size / 1024
      return `${kilobytes.toFixed(2)} KB`
    } else {
      return `${megabytes.toFixed(2)} MB`
    }
  }

  groupFilesByFolder() {
    console.log("TexturesComponent.groupFilesByFolder")
    this.groupedFiles = []; 
    const groups: any = {};

    for (const file of this.files_skybox) {
      const folderName = this.getFolderName(file.Key);

      if (!groups[folderName]) {
        groups[folderName] = {
          folderName: folderName,
          images: {}
        };
      }

      const suffix = this.getSuffix(file.Key);
      groups[folderName].images[suffix] = file;
    }

    this.groupedFiles = Object.values(groups);
    console.log("groupedFiles", this.groupedFiles)
    // Notify Angular about the change
    this.cdr.detectChanges();
  }

  getFolderName(filePath: string): string {
    const parts = filePath.split('/');
    const skyboxIndex = parts.indexOf('skybox');
    if (skyboxIndex !== -1 && skyboxIndex + 1 < parts.length) {
      return parts[skyboxIndex + 1];
    }
    return 'Unknown';
  }
  getSuffix(filePath: string): string {
    const fileName = filePath.split('/').pop() || '';
    const match = fileName.match(/_(n|p)[xyz]/i);
    return match ? match[0].toLowerCase() : '';
  }

  getFilename(filePath: string): string {
    return filePath.split('/').pop() || filePath;
  }

  getFilenameWithEnvExtension(filePath: string): string {
    const filename = filePath.split('/').pop() || filePath;
    return filename.replace(/\.png$/i, '.env');
  }

  // Modal methods
  openSkyboxGroupModal(group: any): void {
    this.selectedSkyboxGroup = group;
    this.showSkyboxModal = true;
  }

  openSingleImageModal(image: S3File, type: string): void {
    this.selectedImage = {
      url: image.DownloadUrl,
      title: `${type} - ${image.Key.split('/').pop()}`,
      filename: image.Key
    };
    this.showImageModal = true;
  }

  closeSkyboxModal(): void {
    this.showSkyboxModal = false;
    this.selectedSkyboxGroup = null;
  }

  closeImageModal(): void {
    this.showImageModal = false;
    this.selectedImage = null;
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKeydown(event: Event): void {
    if (this.showSkyboxModal) {
      this.closeSkyboxModal();
    }
    if (this.showImageModal) {
      this.closeImageModal();
    }
  }
}
